import { useEffect, useState } from 'react';
import { Globe, Plus } from 'lucide-react';
import { domains as domainsApi, type Domain } from '@/lib/api';
import { DashboardLayout } from '@/components/DashboardLayout';
import {
  ButtonLink,
  DataRow,
  EmptyState,
  StatusPill,
} from '@/components/ui';

export default function Domains() {
  const [list, setList] = useState<Domain[] | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    domainsApi
      .list()
      .then((res) => setList(res.domains))
      .catch((e) => setErr(e.message || 'Failed to load domains.'));
  }, []);

  const titleAction = list && list.length > 0 ? (
    <ButtonLink to="/app/domains/add" variant="primary" size="sm" leadingIcon={<Plus size={16} />}>
      Add domain
    </ButtonLink>
  ) : null;

  return (
    <DashboardLayout title="Domains" titleAction={titleAction}>
      <p className="mb-7 max-w-2xl text-[var(--color-on-surface-variant)]">
        Make your links feel like yours. Use{' '}
        <code className="mx-1 rounded-md bg-[var(--color-surface-container)] px-1.5 py-0.5 font-mono text-xs">
          links.yourbrand.com
        </code>{' '}
        instead of the free{' '}
        <code className="mx-1 rounded-md bg-[var(--color-surface-container)] px-1.5 py-0.5 font-mono text-xs">
          go.poof.bio
        </code>{' '}
        subdomain. You bring the domain, we handle SSL.
      </p>

      {err && <p className="mb-4 text-sm text-[var(--color-destructive)]">{err}</p>}

      {list === null && !err && (
        <div className="space-y-3">
          <div className="h-20 animate-pulse rounded-2xl bg-[var(--surface-glass)]" />
          <div className="h-20 animate-pulse rounded-2xl bg-[var(--surface-glass)]" />
        </div>
      )}

      {list && list.length === 0 && (
        <EmptyState
          icon={<Globe size={36} className="text-[var(--color-primary)]" />}
          title="No custom domains yet"
          description="Bring any domain you own — we'll provision the SSL certificate and route every link on it through Poof. Setup takes about 5 minutes."
          cta={
            <ButtonLink to="/app/domains/add" variant="primary" size="lg">
              Add your domain
            </ButtonLink>
          }
        />
      )}

      {list && list.length > 0 && (
        <ul className="space-y-2.5">
          {list.map((d) => {
            const isLive = d.verificationStatus === 'active' && d.sslStatus === 'active';
            return (
              <DataRow
                key={d.id}
                to={`/app/domains/add?id=${d.id}`}
                trailing={
                  <span className="flex items-center gap-1.5 px-4 text-xs font-semibold uppercase tracking-[0.06em] text-[var(--color-primary)]">
                    {isLive ? 'View' : 'Set up →'}
                  </span>
                }
              >
                <DataRow.Stack>
                  <DataRow.Title>
                    <span className="truncate">{d.hostname}</span>
                  </DataRow.Title>
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    <StatusPill
                      tone={pillTone(d.verificationStatus)}
                      label={`DNS: ${d.verificationStatus}`}
                    />
                    <StatusPill
                      tone={pillTone(d.sslStatus)}
                      label={`SSL: ${d.sslStatus}`}
                    />
                  </div>
                </DataRow.Stack>
              </DataRow>
            );
          })}
        </ul>
      )}
    </DashboardLayout>
  );
}

function pillTone(
  status: Domain['sslStatus'],
): 'success' | 'pending' | 'error' | 'neutral' {
  switch (status) {
    case 'active':
      return 'success';
    case 'pending':
      return 'pending';
    case 'failed':
      return 'error';
    default:
      return 'neutral';
  }
}
